import { ApiError } from '../api/client';

/**
 * Same 403/400/fallback idiom `contract-api-errors.ts`'s
 * `genericApiErrorMessage` already applies. Shared by every
 * `menu-calendar`/`holidays` component in this PR (`MenuCalendarEntryModal`,
 * `CopyWeekModal`, `HolidayFormModal`) so none of them re-derives the same
 * `ApiError` narrowing and status fallback (`tasks.md`'s own REFACTOR
 * requirement for this PR: no duplicated 422/409 mapping between
 * components).
 */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** `menu-calendar/spec.md` → codes from `CalendarExceptionFilter` (`design.md` §4), `MenuCalendar` side. */
export function mapMenuCalendarEntryError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 422 && code === 'MENU_CALENDAR_MENU_ARCHIVED') {
    return 'Ese menú está archivado o inactivo y no puede planificarse.';
  }

  if (error.status === 422 && code === 'MENU_POSITION_INACTIVE') {
    return 'Esa posición está inactiva y no puede recibir una entrada.';
  }

  if (error.status === 422 && code === 'MENU_CALENDAR_SERVICE_TYPE_MISMATCH') {
    return 'Ese plato no corresponde al tipo de servicio de la posición elegida.';
  }

  if (error.status === 422 && code === 'MENU_CALENDAR_SERVICE_DATE_PAST') {
    return 'La fecha de servicio debe ser hoy o una fecha futura.';
  }

  if (error.status === 409 && code === 'MENU_CALENDAR_DUPLICATE_ENTRY') {
    return 'Esa posición ya tiene un plato asignado ese día, o ese plato ya está asignado a otra posición ese día.';
  }

  if (error.status === 422 && code === 'MENU_CALENDAR_IMMUTABLE_FIELDS') {
    return 'La fecha de servicio, el menú y la posición no se pueden modificar una vez creada la entrada.';
  }

  if (error.status === 422 && code === 'QUOTA_BELOW_RESERVED') {
    return 'El cupo no puede quedar por debajo de la cantidad ya reservada.';
  }

  if (error.status === 409 && code === 'CANNOT_UNPUBLISH_WITH_RESERVED') {
    return 'No se puede despublicar: esta entrada ya tiene reservas activas.';
  }

  if (error.status === 409 && code === 'CANNOT_DELETE_PUBLISHED_WITH_RESERVED') {
    return 'No se puede eliminar: esta entrada publicada tiene reservas activas.';
  }

  if (error.status === 422 && code === 'DATE_RANGE_TOO_WIDE') {
    return 'El rango de fechas no puede superar los 62 días.';
  }

  return genericApiErrorMessage(error);
}

/** `holidays/spec.md` → codes from `CalendarExceptionFilter` (`design.md` §4), `Holiday` side. */
export function mapHolidayError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'HOLIDAY_DUPLICATE') {
    return 'Ya existe un feriado para esa zona y fecha.';
  }

  if (error.status === 422 && code === 'HOLIDAY_IMMUTABLE_FIELDS') {
    return 'La zona y la fecha no se pueden modificar una vez creado el feriado.';
  }

  return genericApiErrorMessage(error);
}

/** `MISSING_SIN_TACC` / `MISSING_VEGANO` → Spanish copy for `DietaryWarningModal` (`menu-calendar/spec.md` → "Modal de advertencia antes de publicar"). */
export function mapDietaryWarning(code: string): string {
  if (code === 'MISSING_SIN_TACC') {
    return 'Falta opción sin TACC';
  }

  if (code === 'MISSING_VEGANO') {
    return 'Falta opción vegana';
  }

  return code;
}
